import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Plus, Edit2, Trash2, X, Save, Download, Sparkles, Star, Loader2 } from "lucide-react";

const empty = {
  name: "", description: "", address: "", city: "", postal_code: "", department: "",
  phone: "", email: "", website: "", siret: "", legal_form: "",
  rating: 0, review_count: 0, services: [], featured: false,
  listing_status: "referenced",
};

const STATUS_OPTIONS = [
  { value: "referenced",       label: "Référencée",        color: "bg-gray-100 text-gray-700 border-gray-200" },
  { value: "partner",          label: "Partenaire",        color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "hidden",           label: "Masquée",           color: "bg-slate-200 text-slate-700 border-slate-300" },
  { value: "removal_pending",  label: "Retrait en cours",  color: "bg-orange-100 text-orange-700 border-orange-200" },
];

export default function AdminCompanies() {
  const { logout, user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [enrichingId, setEnrichingId] = useState(null);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [enrichMsg, setEnrichMsg] = useState("");

  const load = () => api.get("/companies", { params: { limit: 200, q: search || undefined } }).then((r) => setCompanies(r.data?.items || []));
  useEffect(() => { load(); document.title = "Entreprises Admin"; }, [search]);

  const startCreate = () => { setForm(empty); setCreating(true); setEditing(null); setError(""); };
  const startEdit = (c) => { setForm({ ...empty, ...c }); setEditing(c.id); setCreating(false); setError(""); };
  const cancel = () => { setEditing(null); setCreating(false); setForm(empty); };

  const save = async () => {
    setError("");
    try {
      const payload = { ...form, services: typeof form.services === "string" ? form.services.split(",").map(s => s.trim()).filter(Boolean) : form.services };
      payload.rating = parseFloat(payload.rating) || 0;
      payload.review_count = parseInt(payload.review_count) || 0;
      if (editing) {
        await api.put(`/admin/companies/${editing}`, payload);
      } else {
        await api.post("/admin/companies", payload);
      }
      cancel();
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Erreur lors de l'enregistrement");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette entreprise ?")) return;
    await api.delete(`/admin/companies/${id}`);
    load();
  };

  const changeStatus = async (id, listing_status) => {
    try {
      await api.patch(`/admin/companies/${id}/status`, { listing_status });
      load();
    } catch (err) {
      setError("Erreur lors du changement de statut");
    }
  };

  const enrichOne = async (id) => {
    setEnrichingId(id);
    setEnrichMsg("");
    try {
      const { data } = await api.post(`/admin/companies/${id}/enrich-google`);
      setEnrichMsg(`✓ ${data.updated_fields.length} champ(s) mis à jour depuis Google`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setEnrichMsg(`✗ ${typeof d === "string" ? d : "Erreur"}`);
    } finally {
      setEnrichingId(null);
      setTimeout(() => setEnrichMsg(""), 4000);
    }
  };

  const enrichAll = async () => {
    if (!window.confirm("Enrichir toutes les entreprises avec données manquantes via Google ? (peut prendre 1-2 min)")) return;
    setBulkEnriching(true);
    setEnrichMsg("");
    try {
      const { data } = await api.post("/admin/companies/enrich-all-missing");
      setEnrichMsg(`✓ ${data.enriched_count} entreprises enrichies (${data.failed_count} sans correspondance Google)`);
      load();
    } catch (err) {
      setEnrichMsg("✗ Erreur lors de l'enrichissement");
    } finally {
      setBulkEnriching(false);
      setTimeout(() => setEnrichMsg(""), 6000);
    }
  };

  const cleanupEI = async () => {
    if (!window.confirm("Supprimer toutes les entreprises de type 'Entrepreneur individuel' (personnes physiques) ? Cette action est irréversible.")) return;
    setBulkEnriching(true);
    try {
      const { data } = await api.post("/admin/companies/cleanup-ei");
      setEnrichMsg(`✓ ${data.deleted_count} entrepreneurs individuels supprimés`);
      load();
    } catch (err) {
      setEnrichMsg("✗ Erreur lors du nettoyage");
    } finally {
      setBulkEnriching(false);
      setTimeout(() => setEnrichMsg(""), 6000);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="bg-white border-b border-[#1C1C1C]">
        <div className="container mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="eyebrow bg-[#1D3557] text-white px-2 py-1">Admin</span>
            <h1 className="font-heading font-black text-xl tracking-tight">Entreprises</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/sirene" className="text-sm font-semibold text-[#2C3E50] hover:text-[#2BD391]" data-testid="nav-sirene-from-companies">
              <Download className="w-4 h-4 inline mr-1.5" />Import Sirene
            </Link>
            <Link to="/admin" className="text-sm font-semibold hover:text-[#E07A5F]" data-testid="back-admin">
              <ArrowLeft className="w-4 h-4 inline" /> Dashboard
            </Link>
            <button onClick={logout} className="text-sm font-semibold hover:text-[#C44536]">Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <input
            type="search"
            placeholder="Rechercher (nom, ville)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-[#E5E7EB] focus:border-[#2BD391] focus:ring-1 focus:ring-[#2BD391] outline-none px-4 py-2.5 rounded-full bg-white text-sm w-full max-w-sm"
            data-testid="company-search"
          />
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/admin/corrections" className="btn-outline !border-blue-300 !text-blue-700 hover:!bg-blue-50" data-testid="nav-corrections">
              Demandes de correction
            </Link>
            <button onClick={startCreate} className="btn-primary" data-testid="add-company-btn">
              <Plus className="w-4 h-4" /> Ajouter
            </button>
          </div>
        </div>
        {enrichMsg && (
          <div className="mb-4 p-3 bg-[#E8FBF3] border border-[#2BD391] text-sm text-[#1AA66C] rounded-xl" data-testid="enrich-msg">{enrichMsg}</div>
        )}

        {(creating || editing) && (
          <div className="bg-white border border-[#1C1C1C] shadow-hard-sm p-6 md:p-8 mb-8" data-testid="company-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl">{editing ? "Modifier" : "Nouvelle entreprise"}</h2>
              <button onClick={cancel} data-testid="cancel-form"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Nom *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="f-name" />
              <FormField label="Ville *" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="f-city" />
              <FormField label="Adresse" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="f-address" />
              <FormField label="Code postal" value={form.postal_code} onChange={(v) => setForm({ ...form, postal_code: v })} testid="f-postal" />
              <FormField label="Département" value={form.department} onChange={(v) => setForm({ ...form, department: v })} testid="f-dept" />
              <FormField label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="f-phone" />
              <FormField label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="f-email" />
              <FormField label="Site web" value={form.website} onChange={(v) => setForm({ ...form, website: v })} testid="f-website" />
              <FormField label="SIRET" value={form.siret} onChange={(v) => setForm({ ...form, siret: v })} testid="f-siret" />
              <FormField label="Forme juridique" value={form.legal_form} onChange={(v) => setForm({ ...form, legal_form: v })} testid="f-legal" />
              <FormField label="Note (0-5)" type="number" step="0.1" value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} testid="f-rating" />
              <FormField label="Nb avis" type="number" value={form.review_count} onChange={(v) => setForm({ ...form, review_count: v })} testid="f-reviews" />
              <FormField label="Services (séparés par virgule)" value={Array.isArray(form.services) ? form.services.join(", ") : form.services} onChange={(v) => setForm({ ...form, services: v })} testid="f-services" full />
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-widest font-bold mb-2">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-[#E5E0D8] focus:border-[#1C1C1C] outline-none px-3 py-2 bg-white text-sm" data-testid="f-description" />
              </div>
              <label className="flex items-center gap-2 text-sm md:col-span-2">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="f-featured" />
                Mettre en avant (recommandée)
              </label>
            </div>
            {error && <div className="mt-4 p-3 bg-[#FEE] border border-[#C44536] text-sm text-[#C44536]">{error}</div>}
            <div className="mt-6 flex gap-3">
              <button onClick={save} className="btn-primary" data-testid="save-company"><Save className="w-4 h-4" /> Enregistrer</button>
              <button onClick={cancel} className="btn-outline">Annuler</button>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#1C1C1C] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EFE9]">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Nom</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Ville</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Tél.</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">Statut</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold" />
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="border-t border-[#E5E7EB] hover:bg-[#F7F9FB]" data-testid={`company-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold">{c.featured && <span className="mr-1.5 text-[#2BD391]">★</span>}{c.name}</td>
                  <td className="px-4 py-3">{c.city}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{c.phone || <span className="text-orange-500">manquant</span>}</td>
                  <td className="px-4 py-3">
                    <select
                      value={c.listing_status || "referenced"}
                      onChange={(e) => changeStatus(c.id, e.target.value)}
                      className="text-xs font-semibold border border-[#E5E7EB] rounded-full px-2.5 py-1 bg-white hover:border-[#2BD391] cursor-pointer"
                      data-testid={`status-select-${c.id}`}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(c)} className="text-xs uppercase tracking-widest hover:text-[#2BD391] mr-3" data-testid={`edit-${c.id}`}><Edit2 className="w-3 h-3 inline mr-1" />Modifier</button>
                    <button onClick={() => remove(c.id)} className="text-xs uppercase tracking-widest hover:text-red-600" data-testid={`delete-${c.id}`}><Trash2 className="w-3 h-3 inline mr-1" />Supprimer</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const FormField = ({ label, value, onChange, type = "text", step, testid, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-xs uppercase tracking-widest font-bold mb-2">{label}</label>
    <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-[#E5E0D8] focus:border-[#1C1C1C] outline-none px-3 py-2 bg-white text-sm" data-testid={testid} />
  </div>
);
